import axios from 'axios';
import { getAcquisition } from '../utils/acquisition';

// Every signup carries the partner tracking-link tags (utm_*), if the visitor
// arrived through one — backend/services/acquisition.py records them, first
// touch only. Plain JSON bodies only; anything else passes through unchanged.
const withAcquisition = (body) => {
  const acquisition = getAcquisition();
  if (!acquisition || !body || typeof body !== 'object' || Array.isArray(body)) return body;
  if (typeof FormData !== 'undefined' && body instanceof FormData) return body;
  return body.acquisition ? body : { ...body, acquisition };
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auth API (JWT tokens)
export const authAPI = {
  // Refresh access token using refresh token in HTTP-only cookie
  refreshToken: async () => {
    const response = await axios.post(`${API_URL}/api/auth/refresh`, {}, {
      withCredentials: true  // Include cookies
    });
    return response.data;
  },

  // Logout and revoke refresh token
  logout: async (token) => {
    const response = await axios.post(`${API_URL}/api/auth/logout`, {}, {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true
    });
    return response.data;
  },

  // Get current session info
  getSession: async (token) => {
    const response = await axios.get(`${API_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Get active sessions
  getSessions: async (token) => {
    const response = await axios.get(`${API_URL}/api/auth/sessions`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Revoke a specific session
  revokeSession: async (sessionId, token) => {
    const response = await axios.delete(`${API_URL}/api/auth/sessions/${sessionId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  }
};

// Property API
export const propertyAPI = {
  getPublishedProperties: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.city && filters.city !== 'all') params.append('city', filters.city);
    if (filters.propertyType && filters.propertyType !== 'all') params.append('propertyType', filters.propertyType);
    if (filters.minPrice) params.append('minPrice', filters.minPrice);
    if (filters.maxPrice) params.append('maxPrice', filters.maxPrice);
    if (filters.guests) params.append('guests', filters.guests);
    
    const response = await axios.get(`${API_URL}/api/properties/?${params.toString()}`);
    return response.data;
  },

  getProperty: async (propertyId) => {
    const response = await axios.get(`${API_URL}/api/properties/${propertyId}`);
    return response.data;
  },

  getHostProperties: async (token) => {
    const response = await axios.get(`${API_URL}/api/properties/host/my-properties`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  createProperty: async (propertyData, token) => {
    const formData = new FormData();
    formData.append('property_data', JSON.stringify(propertyData));
    
    const response = await axios.post(`${API_URL}/api/properties/`, formData, {
      headers: { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  updateProperty: async (propertyId, propertyData, token) => {
    const formData = new FormData();
    formData.append('property_data', JSON.stringify(propertyData));

    const response = await axios.put(`${API_URL}/api/properties/${propertyId}`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // London 90-night rule (Deregulation Act 2015): nights-used counter for the host.
  getStrCompliance: async (token) => {
    const response = await axios.get(`${API_URL}/api/bookings/host/str-compliance`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Host self-declares planning consent for short-term letting (exempts the 90-night cap).
  declarePlanningConsent: async (hasPlanningConsent, reference, token) => {
    const response = await axios.post(
      `${API_URL}/api/bookings/host/str-planning-consent`,
      { hasPlanningConsent, reference },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  updatePropertyStatus: async (propertyId, status, token) => {
    const response = await axios.patch(
      `${API_URL}/api/properties/${propertyId}/status?status=${status}`,
      {},
      { headers: { Authorization: `Bearer ${token}` }}
    );
    return response.data;
  },

  deleteProperty: async (propertyId, token) => {
    const response = await axios.delete(`${API_URL}/api/properties/${propertyId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  uploadImages: async (propertyId, files, token) => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    
    const response = await axios.post(
      `${API_URL}/api/properties/${propertyId}/images`,
      formData,
      {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return response.data;
  },

  updateDispatchSettings: async (propertyId, settings, token) => {
    const response = await axios.patch(
      `${API_URL}/api/properties/${propertyId}/dispatch-settings`,
      settings,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  toggleSmartLock: async (propertyId, hasSmartLock, token) => {
    const response = await axios.patch(
      `${API_URL}/api/properties/${propertyId}/smart-lock`,
      { hasSmartLock },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Parses a description the host pastes in into listing fields. Suggestions
  // only — nothing is saved until the host submits the form themselves.
  parseDescription: async (description, token) => {
    const response = await axios.post(
      `${API_URL}/api/properties/parse-description`,
      { description },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },
};

// Booking API
export const bookingAPI = {
  getHostBookings: async (token) => {
    const response = await axios.get(`${API_URL}/api/bookings/host`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getGuestBookings: async (token) => {
    const response = await axios.get(`${API_URL}/api/bookings/guest`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getPropertyBookings: async (propertyId, token) => {
    const response = await axios.get(`${API_URL}/api/bookings/property/${propertyId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getBooking: async (bookingId) => {
    const response = await axios.get(`${API_URL}/api/bookings/${bookingId}`);
    return response.data;
  },

  createBooking: async (bookingData, token) => {
    const response = await axios.post(`${API_URL}/api/bookings/`, bookingData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  updateBookingStatus: async (bookingId, status, token) => {
    const response = await axios.patch(
      `${API_URL}/api/bookings/${bookingId}/status?status=${status}`,
      {},
      { headers: { Authorization: `Bearer ${token}` }}
    );
    return response.data;
  },

  // Host accepts a same-day late-window booking request. The booking
  // transitions from pending_host_approval -> pending_payment so the
  // guest can pay.
  hostApproveBooking: async (bookingId, token) => {
    const response = await axios.post(
      `${API_URL}/api/bookings/${bookingId}/host-approve`,
      {},
      { headers: { Authorization: `Bearer ${token}` }}
    );
    return response.data;
  },

  // Host declines a same-day late-window booking request.
  hostDeclineBooking: async (bookingId, token, reason = '') => {
    const response = await axios.post(
      `${API_URL}/api/bookings/${bookingId}/host-decline`,
      { reason },
      { headers: { Authorization: `Bearer ${token}` }}
    );
    return response.data;
  },

  deleteBooking: async (bookingId, token) => {
    const response = await axios.delete(`${API_URL}/api/bookings/${bookingId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getBookingOptions: async (propertyId, checkIn, checkOut) => {
    const response = await axios.get(
      `${API_URL}/api/bookings/options/${propertyId}?check_in=${checkIn}&check_out=${checkOut}`
    );
    return response.data;
  },

  completeRtrCheck: async (bookingId, token, notes = '') => {
    const response = await axios.patch(
      `${API_URL}/api/bookings/${bookingId}/rtr-check`,
      { notes },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },
};

// Calendar API
export const calendarAPI = {
  getPropertyCalendar: async (propertyId, token) => {
    const response = await axios.get(`${API_URL}/api/calendar/property/${propertyId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  blockDates: async (blockData, token) => {
    const response = await axios.post(`${API_URL}/api/calendar/block`, blockData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  unblockDates: async (blockId, token) => {
    const response = await axios.delete(`${API_URL}/api/calendar/block/${blockId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getAvailability: async (propertyId) => {
    const response = await axios.get(`${API_URL}/api/calendar/availability/${propertyId}`);
    return response.data;
  },

  // ── Cross-platform calendar sync (iCal) ──
  getIcalLink: async (propertyId, token) => {
    const response = await axios.get(`${API_URL}/api/calendar/ical-link/${propertyId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  listIcalImports: async (propertyId, token) => {
    const response = await axios.get(`${API_URL}/api/calendar/ical-import/${propertyId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  addIcalImport: async (propertyId, data, token) => {
    const response = await axios.post(`${API_URL}/api/calendar/ical-import/${propertyId}`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  removeIcalImport: async (propertyId, url, token) => {
    const response = await axios.delete(`${API_URL}/api/calendar/ical-import/${propertyId}`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { url }
    });
    return response.data;
  },

  syncIcalNow: async (propertyId, token) => {
    const response = await axios.post(`${API_URL}/api/calendar/ical-sync/${propertyId}`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Price Override endpoints
  createPriceOverride: async (overrideData, token) => {
    const response = await axios.post(`${API_URL}/api/calendar/price-override`, overrideData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getPriceOverrides: async (propertyId, token) => {
    const response = await axios.get(`${API_URL}/api/calendar/price-overrides/${propertyId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  deletePriceOverride: async (overrideId, token) => {
    const response = await axios.delete(`${API_URL}/api/calendar/price-override/${overrideId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Get effective price for a date range
  getEffectivePrice: async (propertyId, checkIn, checkOut) => {
    const response = await axios.get(
      `${API_URL}/api/calendar/effective-price/${propertyId}?check_in=${checkIn}&check_out=${checkOut}`
    );
    return response.data;
  },

  // Get AI-powered smart pricing suggestions
  getSmartPricing: async (propertyId, token) => {
    const response = await axios.get(`${API_URL}/api/calendar/smart-pricing/${propertyId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Toggle smart pricing enabled/disabled for a property
  toggleSmartPricing: async (propertyId, enabled, token) => {
    const response = await axios.post(`${API_URL}/api/calendar/smart-pricing/${propertyId}/toggle`, { enabled }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Get smart pricing enabled status for a property
  getSmartPricingStatus: async (propertyId, token) => {
    const response = await axios.get(`${API_URL}/api/calendar/smart-pricing/${propertyId}/status`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  }
};

// Payments API
export const paymentsAPI = {
  createCheckoutSession: async (bookingId, originUrl, token) => {
    const response = await axios.post(`${API_URL}/api/payments/checkout/create`, {
      bookingId,
      originUrl
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getPaymentStatus: async (sessionId, token) => {
    const response = await axios.get(`${API_URL}/api/payments/checkout/status/${sessionId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getBookingTransaction: async (bookingId, token) => {
    const response = await axios.get(`${API_URL}/api/payments/booking/${bookingId}/transaction`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  cancelPendingBooking: async (bookingId, token) => {
    const response = await axios.post(`${API_URL}/api/payments/booking/${bookingId}/cancel`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Test/demo payment - bypasses Stripe for testing
  testCheckout: async (bookingId, token) => {
    const response = await axios.post(`${API_URL}/api/payments/test-checkout/${bookingId}`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Refund preview for a paid booking. Read-only; safe to call repeatedly
  // while the guest is reading the cancellation modal. Returns the policy
  // summary + a breakdown of guest_refund / host_payout / platform_keeps
  // so the frontend can show the exact number before the guest confirms.
  getRefundPreview: async (bookingId, token) => {
    const response = await axios.get(
      `${API_URL}/api/payments/booking/${bookingId}/refund-preview`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Guest-initiated cancellation of a *paid* booking. Caller MUST pass the
  // refund amount it just saw on the preview — if the server's number has
  // changed (policy threshold crossed in the intervening seconds), the
  // backend refuses with 409 so the guest can see the updated number.
  guestCancelWithRefund: async (bookingId, confirmRefundAmount, token) => {
    const response = await axios.post(
      `${API_URL}/api/payments/booking/cancel-with-refund`,
      { bookingId, confirmRefundAmount },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Pay an accepted service-request quote. Stripe Checkout Session
  // is created; we redirect the client to checkoutUrl.
  createServiceRequestCheckout: async (serviceRequestId, originUrl, token) => {
    const response = await axios.post(
      `${API_URL}/api/payments/service-request/checkout/create`,
      { serviceRequestId, originUrl },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Pay a food order. Replaces the cash-on-delivery flow.
  createFoodOrderCheckout: async (orderId, originUrl, token) => {
    const response = await axios.post(
      `${API_URL}/api/payments/food-order/checkout/create`,
      { orderId, originUrl },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Pay a completed ride.
  createRideCheckout: async (rideId, originUrl, token) => {
    const response = await axios.post(
      `${API_URL}/api/payments/ride/checkout/create`,
      { rideId, originUrl },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  }
};

// Reviews API
export const reviewsAPI = {
  getPropertyReviews: async (propertyId) => {
    const response = await axios.get(`${API_URL}/api/reviews/property/${propertyId}`);
    return response.data;
  },

  getHostReviews: async (hostId) => {
    const response = await axios.get(`${API_URL}/api/reviews/host/${hostId}`);
    return response.data;
  },

  getMyReviews: async (token) => {
    const response = await axios.get(`${API_URL}/api/reviews/my-reviews`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  canReview: async (bookingId, token) => {
    const response = await axios.get(`${API_URL}/api/reviews/can-review/${bookingId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  createReview: async (reviewData, token) => {
    const response = await axios.post(`${API_URL}/api/reviews/`, reviewData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  }
};

// Messages API
export const messagesAPI = {
  getConversations: async (token, mode = null) => {
    const params = mode ? { mode } : {};
    const response = await axios.get(`${API_URL}/api/messages/conversations`, {
      headers: { Authorization: `Bearer ${token}` },
      params
    });
    return response.data;
  },

  // NEW: Get conversations grouped by listing for host inbox
  getConversationsByListing: async (token) => {
    const response = await axios.get(`${API_URL}/api/messages/by-listing`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // NEW: Get unread counts per listing
  getUnreadByListing: async (token) => {
    const response = await axios.get(`${API_URL}/api/messages/unread-by-listing`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // NEW: Mark conversation as read
  markConversationRead: async (conversationId, token) => {
    const response = await axios.post(`${API_URL}/api/messages/conversations/${conversationId}/mark-read`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getConversation: async (conversationId, token) => {
    const response = await axios.get(`${API_URL}/api/messages/conversations/${conversationId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  createConversation: async (convData, token) => {
    const response = await axios.post(`${API_URL}/api/messages/conversations`, convData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  sendMessage: async (messageData, token) => {
    const response = await axios.post(`${API_URL}/api/messages/send`, messageData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getUnreadCount: async (token) => {
    const response = await axios.get(`${API_URL}/api/messages/unread`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  resolveLostItem: async (conversationId, token) => {
    const response = await axios.post(`${API_URL}/api/messages/conversations/${conversationId}/resolve`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  sendPhotoMessage: async (conversationId, blob, messageType, metadata, token) => {
    const formData = new FormData();
    formData.append('file', blob, `evidence_${Date.now()}.jpg`);
    formData.append('conversationId', conversationId);
    formData.append('messageType', messageType);
    formData.append('photoMetadata', JSON.stringify(metadata));
    const response = await axios.post(`${API_URL}/api/messages/send-photo`, formData, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }
};

// Tasks API
export const tasksAPI = {
  getTasks: async (token, status = null, taskType = null) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (taskType) params.append('task_type', taskType);
    
    const response = await axios.get(`${API_URL}/api/tasks/?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getPropertyTasks: async (propertyId, token) => {
    const response = await axios.get(`${API_URL}/api/tasks/property/${propertyId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getTask: async (taskId, token) => {
    const response = await axios.get(`${API_URL}/api/tasks/${taskId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  createTask: async (taskData, token) => {
    const response = await axios.post(`${API_URL}/api/tasks/`, taskData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  updateTask: async (taskId, taskData, token) => {
    const response = await axios.patch(`${API_URL}/api/tasks/${taskId}`, taskData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  updateTaskStatus: async (taskId, status, token) => {
    const response = await axios.patch(
      `${API_URL}/api/tasks/${taskId}/status?status=${status}`,
      {},
      { headers: { Authorization: `Bearer ${token}` }}
    );
    return response.data;
  },

  // Host flags completed work as not satisfactory and asks for a redo.
  requestRework: async (taskId, reason, token) => {
    const response = await axios.post(
      `${API_URL}/api/tasks/${taskId}/rework`,
      { reason },
      { headers: { Authorization: `Bearer ${token}` }}
    );
    return response.data;
  },

  uploadCompletionImages: async (taskId, files, token) => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    
    const response = await axios.post(
      `${API_URL}/api/tasks/${taskId}/images`,
      formData,
      {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return response.data;
  },

  completeTask: async (taskId, completionData, token) => {
    const response = await axios.post(
      `${API_URL}/api/tasks/${taskId}/complete`,
      completionData,
      { headers: { Authorization: `Bearer ${token}` }}
    );
    return response.data;
  },

  deleteTask: async (taskId, token) => {
    const response = await axios.delete(`${API_URL}/api/tasks/${taskId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getCleaners: async (token) => {
    const response = await axios.get(`${API_URL}/api/tasks/cleaners/list`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  addCleaner: async (cleanerData, token) => {
    const response = await axios.post(`${API_URL}/api/tasks/cleaners`, cleanerData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  updateVendor: async (vendorId, vendorData, token) => {
    const response = await axios.patch(`${API_URL}/api/tasks/cleaners/${vendorId}`, vendorData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  deleteVendor: async (vendorId, token) => {
    const response = await axios.delete(`${API_URL}/api/tasks/cleaners/${vendorId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Vendor Messaging
  getVendorMessages: async (token) => {
    const response = await axios.get(`${API_URL}/api/tasks/vendor-messages`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  getVendorThread: async (vendorId, token) => {
    const response = await axios.get(`${API_URL}/api/tasks/vendor-messages/${vendorId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  sendVendorMessage: async (vendorId, text, taskRef, token) => {
    const response = await axios.post(`${API_URL}/api/tasks/vendor-messages/${vendorId}`, { text, taskRef }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  verifyLinenPin: async (taskId, pin, token) => {
    const response = await axios.post(
      `${API_URL}/api/tasks/${taskId}/verify-linen`,
      { pin },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  getHostSettings: async (token) => {
    const response = await axios.get(`${API_URL}/api/tasks/settings/host`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  updateHostSettings: async (settings, token) => {
    const response = await axios.patch(`${API_URL}/api/tasks/settings/host`, settings, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
};

// User/Auth API
export const userAPI = {
  signup: async (userData) => {
    const response = await axios.post(`${API_URL}/api/users/signup`, withAcquisition(userData));
    return response.data;
  },

  login: async (loginData) => {
    const response = await axios.post(`${API_URL}/api/users/login`, loginData);
    return response.data;
  },

  getCurrentUser: async (token) => {
    const response = await axios.get(`${API_URL}/api/users/me`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  acceptResponsibility: async (version, token) => {
    const response = await axios.post(
      `${API_URL}/api/users/accept-responsibility`,
      { version },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  completeHostOnboarding: async (onboardingData, token) => {
    const response = await axios.post(
      `${API_URL}/api/users/complete-host-onboarding`,
      onboardingData,
      { headers: { Authorization: `Bearer ${token}` }}
    );
    return response.data;
  },

  companyHostSignup: async (signupData) => {
    const response = await axios.post(`${API_URL}/api/users/company-host-signup`, withAcquisition(signupData));
    return response.data;
  },

  updateActiveMode: async (mode, token) => {
    const response = await axios.patch(
      `${API_URL}/api/users/update-mode?mode=${mode}`,
      {},
      { headers: { Authorization: `Bearer ${token}` }}
    );
    return response.data;
  },

  getProfile: async (token) => {
    const response = await axios.get(`${API_URL}/api/users/profile`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  updateProfile: async (profileData, token) => {
    const response = await axios.patch(
      `${API_URL}/api/users/profile`,
      profileData,
      { headers: { Authorization: `Bearer ${token}` }}
    );
    return response.data;
  },

  findUserByEmail: async (email) => {
    const response = await axios.get(`${API_URL}/api/users/debug/find-user?email=${encodeURIComponent(email)}`);
    return response.data;
  },

  restoreHostAccess: async (userId) => {
    const response = await axios.post(`${API_URL}/api/users/debug/restore-host/${userId}`);
    return response.data;
  },

  uploadProfilePhoto: async (file, token) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API_URL}/api/users/profile-photo/upload`, formData, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  // 2FA endpoints
  get2FAStatus: async (token) => {
    const response = await axios.get(`${API_URL}/api/auth/2fa/status`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  setup2FA: async (token) => {
    const response = await axios.post(`${API_URL}/api/auth/2fa/setup`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  verify2FA: async (code, token) => {
    const response = await axios.post(`${API_URL}/api/auth/2fa/verify`, { code }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  disable2FA: async (code, token) => {
    const response = await axios.post(`${API_URL}/api/auth/2fa/disable`, { code }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  login2FA: async (userId, code) => {
    const response = await axios.post(`${API_URL}/api/users/login/2fa`, { userId, code });
    return response.data;
  },

  // Add a partner role (landlord / service_provider / delivery_rider / ...)
  // to the currently-authenticated user. Used by the AddRolePage flow for
  // roles that don't have a dedicated compliance form.
  addRole: async (role, token) => {
    const response = await axios.post(`${API_URL}/api/users/me/add-role`, { role }, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    return response.data;
  },
};

// Data Model API (Legal Entities, Host Profiles, Profiles)
export const dataModelAPI = {
  // Migration
  migrateDryRun: async (token) => {
    const response = await axios.post(`${API_URL}/api/data-model/migrate/dry-run`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  migrateExecute: async (token) => {
    const response = await axios.post(`${API_URL}/api/data-model/migrate/execute`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getMigrationStatus: async (token) => {
    const response = await axios.get(`${API_URL}/api/data-model/migrate/status`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Full user data
  getFullUserData: async (token) => {
    const response = await axios.get(`${API_URL}/api/data-model/me/full`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Legal Entity
  getLegalEntity: async (token) => {
    const response = await axios.get(`${API_URL}/api/data-model/legal-entities/me`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  createLegalEntity: async (data, token) => {
    const response = await axios.post(`${API_URL}/api/data-model/legal-entities`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Host Profile
  getHostProfile: async (token) => {
    const response = await axios.get(`${API_URL}/api/data-model/host-profiles/me`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  createHostProfile: async (data, token) => {
    const response = await axios.post(`${API_URL}/api/data-model/host-profiles`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Profile (UI settings)
  getProfile: async (token) => {
    const response = await axios.get(`${API_URL}/api/data-model/profiles/me`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  updateProfile: async (data, token) => {
    const response = await axios.patch(`${API_URL}/api/data-model/profiles/me`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  }
};

// Service Provider API
export const providerAPI = {
  // Registration
  register: async (data) => {
    const response = await axios.post(`${API_URL}/api/providers/register`, withAcquisition(data));
    return response.data;
  },

  // Dashboard
  getDashboard: async (token) => {
    const response = await axios.get(`${API_URL}/api/providers/dashboard`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Tasks
  getTasks: async (token, status = null) => {
    const url = status 
      ? `${API_URL}/api/providers/tasks?status=${status}`
      : `${API_URL}/api/providers/tasks`;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getTask: async (taskId, token) => {
    const response = await axios.get(`${API_URL}/api/providers/tasks/${taskId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  acceptTask: async (taskId, token) => {
    const response = await axios.patch(`${API_URL}/api/providers/tasks/${taskId}/accept`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  startTask: async (taskId, token) => {
    const response = await axios.patch(`${API_URL}/api/providers/tasks/${taskId}/start`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  completeTask: async (taskId, data, token) => {
    const response = await axios.patch(`${API_URL}/api/providers/tasks/${taskId}/complete`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  uploadTaskPhotos: async (taskId, formData, token) => {
    const response = await axios.post(`${API_URL}/api/providers/tasks/${taskId}/photos`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  uploadEvidencePhoto: async (taskId, blob, metadata, label, token) => {
    const formData = new FormData();
    formData.append('file', blob, `evidence_${Date.now()}.jpg`);
    formData.append('photoMetadata', JSON.stringify(metadata));
    formData.append('label', label || 'evidence');
    const response = await axios.post(`${API_URL}/api/providers/tasks/${taskId}/photos`, formData, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  // Availability
  getAvailability: async (token) => {
    const response = await axios.get(`${API_URL}/api/providers/availability`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  updateAvailability: async (availability, token) => {
    const response = await axios.put(`${API_URL}/api/providers/availability`, availability, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Profile
  getProfile: async (token) => {
    const response = await axios.get(`${API_URL}/api/providers/profile`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  updateProfile: async (data, token) => {
    const response = await axios.patch(`${API_URL}/api/providers/profile`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // For hosts - list providers
  listProviders: async (token, serviceType = null) => {
    const url = serviceType
      ? `${API_URL}/api/providers/list?service_type=${serviceType}`
      : `${API_URL}/api/providers/list`;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // For hosts - assign provider to task
  assignProvider: async (taskId, providerId, token) => {
    const response = await axios.post(`${API_URL}/api/providers/assign/${taskId}`,
      { providerId },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Compliance Document Vault
  uploadDocument: async (docType, file, token) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API_URL}/api/providers/documents/${docType}`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  adminVerifyDocument: async (providerUserId, docType, body, token) => {
    const response = await axios.post(
      `${API_URL}/api/providers/admin/verify-document/${providerUserId}/${docType}`,
      body,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  uploadPortfolioPhoto: async (file, token) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API_URL}/api/providers/portfolio/upload`, formData, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  deletePortfolioPhoto: async (filename, token) => {
    const response = await axios.delete(`${API_URL}/api/providers/portfolio/${filename}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  uploadProfilePhoto: async (file, token) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API_URL}/api/providers/profile-photo/upload`, formData, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  getEarnings: async (token) => {
    const response = await axios.get(`${API_URL}/api/providers/earnings`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getAnalytics: async (token) => {
    const response = await axios.get(`${API_URL}/api/providers/analytics`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Job Board / Marketplace
  getMarketplaceJobs: async (token, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.category) queryParams.append('category', params.category);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    const response = await axios.get(`${API_URL}/api/providers/marketplace?${queryParams.toString()}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
};

// Service Marketplace API (Provider Discovery)
export const serviceMarketplaceAPI = {
  getCategories: async () => {
    const response = await axios.get(`${API_URL}/api/service-categories`);
    return response.data;
  },

  browseProviders: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.category) params.append('category', filters.category);
    if (filters.subcategory) params.append('subcategory', filters.subcategory);
    if (filters.postcode) params.append('postcode', filters.postcode);
    if (filters.min_rating) params.append('min_rating', filters.min_rating);
    if (filters.max_price) params.append('max_price', filters.max_price);
    if (filters.verified_only) params.append('verified_only', 'true');
    if (filters.sort_by) params.append('sort_by', filters.sort_by);
    if (filters.page) params.append('page', filters.page);
    const response = await axios.get(`${API_URL}/api/marketplace/services/providers/browse?${params}`);
    return response.data;
  },

  getPublicProfile: async (providerId) => {
    const response = await axios.get(`${API_URL}/api/marketplace/services/providers/${providerId}/public`);
    return response.data;
  },

  getFeaturedProviders: async () => {
    const response = await axios.get(`${API_URL}/api/marketplace/services/providers/featured`);
    return response.data;
  },
};

// Provider Reviews API
export const providerReviewsAPI = {
  create: async (data, token) => {
    const response = await axios.post(`${API_URL}/api/provider-reviews/`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getForProvider: async (providerId) => {
    const response = await axios.get(`${API_URL}/api/provider-reviews/provider/${providerId}`);
    return response.data;
  },

  respond: async (reviewId, responseText, token) => {
    const response = await axios.post(`${API_URL}/api/provider-reviews/${reviewId}/respond`, { response: responseText }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  canReview: async (jobId, token) => {
    const response = await axios.get(`${API_URL}/api/provider-reviews/can-review/${jobId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
};

// Service Requests API (Quotes)
export const serviceRequestAPI = {
  create: async (data, token) => {
    const response = await axios.post(`${API_URL}/api/service-requests/`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getMyRequests: async (token, status = null) => {
    const params = status ? `?status=${status}` : '';
    const response = await axios.get(`${API_URL}/api/service-requests/my${params}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getRequest: async (requestId, token) => {
    const response = await axios.get(`${API_URL}/api/service-requests/${requestId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getAvailable: async (token, category = null) => {
    const params = category ? `?category=${category}` : '';
    const response = await axios.get(`${API_URL}/api/service-requests/available/list${params}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  submitQuote: async (requestId, data, token) => {
    const response = await axios.post(`${API_URL}/api/service-requests/${requestId}/quote`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  acceptQuote: async (requestId, quoteId, token) => {
    const response = await axios.post(`${API_URL}/api/service-requests/${requestId}/accept-quote/${quoteId}`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  cancel: async (requestId, token) => {
    const response = await axios.post(`${API_URL}/api/service-requests/${requestId}/cancel`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
};

// Marketplace API (R2R Deals)
export const marketplaceAPI = {
  // Landlord registration
  registerLandlord: async (data) => {
    const response = await axios.post(`${API_URL}/api/marketplace/landlord/register`, withAcquisition(data));
    return response.data;
  },

  // Landlord dashboard
  getLandlordDashboard: async (token) => {
    const response = await axios.get(`${API_URL}/api/marketplace/landlord/dashboard`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Listings - Landlord
  createListing: async (data, token) => {
    const response = await axios.post(`${API_URL}/api/marketplace/listings`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getMyListings: async (token, status = null) => {
    const url = status
      ? `${API_URL}/api/marketplace/listings/my?status=${status}`
      : `${API_URL}/api/marketplace/listings/my`;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getListing: async (listingId, token) => {
    const response = await axios.get(`${API_URL}/api/marketplace/listings/${listingId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  updateListing: async (listingId, data, token) => {
    const response = await axios.patch(`${API_URL}/api/marketplace/listings/${listingId}`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  deleteListing: async (listingId, token) => {
    const response = await axios.delete(`${API_URL}/api/marketplace/listings/${listingId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  uploadListingImages: async (listingId, formData, token) => {
    const response = await axios.post(`${API_URL}/api/marketplace/listings/${listingId}/images`, formData, {
      headers: { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // Browse listings (for hosts)
  browseListings: async (filters, token) => {
    const params = new URLSearchParams();
    if (filters.city) params.append('city', filters.city);
    if (filters.min_rent) params.append('min_rent', filters.min_rent);
    if (filters.max_rent) params.append('max_rent', filters.max_rent);
    if (filters.bedrooms) params.append('bedrooms', filters.bedrooms);
    
    const url = `${API_URL}/api/marketplace/browse${params.toString() ? '?' + params.toString() : ''}`;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Offers - Host
  createOffer: async (data, token) => {
    const response = await axios.post(`${API_URL}/api/marketplace/offers`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getSentOffers: async (token, status = null) => {
    const url = status
      ? `${API_URL}/api/marketplace/offers/sent?status=${status}`
      : `${API_URL}/api/marketplace/offers/sent`;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  withdrawOffer: async (offerId, token) => {
    const response = await axios.patch(`${API_URL}/api/marketplace/offers/${offerId}/withdraw`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Offers - Landlord
  getReceivedOffers: async (token, status = null) => {
    const url = status
      ? `${API_URL}/api/marketplace/offers/received?status=${status}`
      : `${API_URL}/api/marketplace/offers/received`;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  acceptOffer: async (offerId, token) => {
    const response = await axios.patch(`${API_URL}/api/marketplace/offers/${offerId}/accept`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // What each side pays and receives on an R2R deal, before anyone commits.
  // The host's total sits above the rent they agreed with the landlord, and
  // the landlord receives below it, so both figures are shown before the
  // decision rather than discovered when the money moves.
  getDealBreakdown: async (offerId, token) => {
    const response = await axios.get(`${API_URL}/api/payments/r2r-deal/${offerId}/breakdown`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // The single charge that concludes the deal: one month's rent plus Ariv's
  // 2%. No fee is earned and no referral is owed until this succeeds.
  payDeal: async (offerId, originUrl, token) => {
    const response = await axios.post(`${API_URL}/api/payments/r2r-deal/checkout/create`, { offerId, originUrl }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  rejectOffer: async (offerId, data, token) => {
    const response = await axios.patch(`${API_URL}/api/marketplace/offers/${offerId}/reject`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Convert accepted deal to property
  convertToProperty: async (offerId, data, token) => {
    const response = await axios.post(`${API_URL}/api/marketplace/offers/${offerId}/convert-to-property`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  }
};


// Restaurant API
export const restaurantAPI = {
  // Registration
  register: async (data) => {
    const response = await axios.post(`${API_URL}/api/restaurants/register`, withAcquisition(data));
    return response.data;
  },

  // Dashboard
  getDashboard: async (token) => {
    const response = await axios.get(`${API_URL}/api/restaurants/dashboard`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Launch-phase: how close is my area to having enough riders for Ariv
  // delivery? Returns { city, riderCount, threshold, remaining, isReady,
  // referralCode } so the dashboard can render a coming-soon banner.
  getCoverageStatus: async (token) => {
    const response = await axios.get(`${API_URL}/api/restaurants/coverage-status`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getMenuPerformance: async (token) => {
    const response = await axios.get(`${API_URL}/api/restaurants/analytics/menu-performance`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Menu Management
  getMenuItems: async (token) => {
    const response = await axios.get(`${API_URL}/api/restaurants/menu`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getMenuItem: async (itemId, token) => {
    const response = await axios.get(`${API_URL}/api/restaurants/menu/${itemId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  createMenuItem: async (data, token) => {
    const response = await axios.post(`${API_URL}/api/restaurants/menu`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  updateMenuItem: async (itemId, data, token) => {
    const response = await axios.patch(`${API_URL}/api/restaurants/menu/${itemId}`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  deleteMenuItem: async (itemId, token) => {
    const response = await axios.delete(`${API_URL}/api/restaurants/menu/${itemId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Orders (Restaurant side)
  getOrders: async (token, status = null) => {
    const url = status 
      ? `${API_URL}/api/restaurants/orders?status=${status}`
      : `${API_URL}/api/restaurants/orders`;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  updateOrderStatus: async (orderId, status, token, extras = {}) => {
    const response = await axios.patch(`${API_URL}/api/restaurants/orders/${orderId}/status`,
      { status, ...extras },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Profile
  getProfile: async (token) => {
    const response = await axios.get(`${API_URL}/api/restaurants/profile`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  updateProfile: async (data, token) => {
    const response = await axios.patch(`${API_URL}/api/restaurants/profile`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Notification Preferences
  getNotificationPreferences: async (token) => {
    const response = await axios.get(`${API_URL}/api/restaurants/notification-preferences`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  updateNotificationPreferences: async (prefs, token) => {
    const response = await axios.patch(`${API_URL}/api/restaurants/notification-preferences`, prefs, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Image Upload
  uploadImage: async (file, token) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API_URL}/api/restaurants/upload`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // Guest-facing endpoints
  browseRestaurants: async (city, token) => {
    const url = city 
      ? `${API_URL}/api/restaurants/browse?city=${encodeURIComponent(city)}`
      : `${API_URL}/api/restaurants/browse`;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getRestaurantMenu: async (restaurantId, token) => {
    const response = await axios.get(`${API_URL}/api/restaurants/browse/${restaurantId}/menu`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  placeOrder: async (orderData, token) => {
    const response = await axios.post(`${API_URL}/api/restaurants/orders/place`, orderData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getMyOrders: async (token) => {
    const response = await axios.get(`${API_URL}/api/restaurants/orders/my`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getMyOrder: async (orderId, token) => {
    const response = await axios.get(`${API_URL}/api/restaurants/orders/my/${orderId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  cancelOrder: async (orderId, token) => {
    const response = await axios.patch(`${API_URL}/api/restaurants/orders/my/${orderId}/cancel`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  rateOrder: async (orderId, rating, comment, token) => {
    const response = await axios.post(`${API_URL}/api/restaurants/orders/my/${orderId}/rate`,
      { rating, comment },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  trackOrder: async (orderId, token) => {
    const response = await axios.get(`${API_URL}/api/restaurants/orders/my/${orderId}/track`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Enhanced browse with filters
  browseRestaurantsFiltered: async (filters, token) => {
    const params = new URLSearchParams();
    if (filters.city) params.append('city', filters.city);
    if (filters.businessType) params.append('businessType', filters.businessType);
    if (filters.cuisineType) params.append('cuisineType', filters.cuisineType);
    const response = await axios.get(`${API_URL}/api/restaurants/browse?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Restaurant side — update order with estimated time
  updateOrderStatusWithTime: async (orderId, status, estimatedTime, token) => {
    const response = await axios.patch(`${API_URL}/api/restaurants/orders/${orderId}/status`,
      { status, estimatedTime },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Promo Codes (restaurant side)
  getPromoCodes: async (token) => {
    const response = await axios.get(`${API_URL}/api/restaurants/promos`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  createPromoCode: async (data, token) => {
    const response = await axios.post(`${API_URL}/api/restaurants/promos`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  updatePromoCode: async (promoId, data, token) => {
    const response = await axios.patch(`${API_URL}/api/restaurants/promos/${promoId}`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  deletePromoCode: async (promoId, token) => {
    const response = await axios.delete(`${API_URL}/api/restaurants/promos/${promoId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Promo code validation (guest side)
  validatePromoCode: async (code, restaurantId, subtotal, token) => {
    const response = await axios.post(`${API_URL}/api/restaurants/promos/validate`,
      { code, restaurantId, subtotal },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Item availability toggle
  toggleItemAvailability: async (itemId, isAvailable, token) => {
    const response = await axios.patch(`${API_URL}/api/restaurants/menu/${itemId}/availability`,
      { isAvailable },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Review responses
  respondToReview: async (orderId, responseText, token) => {
    const response = await axios.post(`${API_URL}/api/restaurants/orders/${orderId}/review-response`,
      { response: responseText },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Payout summary
  getPayoutSummary: async (period, token) => {
    const response = await axios.get(`${API_URL}/api/restaurants/payouts/summary?period=${period}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Refund request (legacy — kept for backwards compat)
  requestRefund: async (orderId, issueType, reason, token) => {
    const response = await axios.post(`${API_URL}/api/restaurants/orders/my/${orderId}/refund-request`,
      { issueType, reason },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Report a problem (new Just Eat-style workflow)
  reportProblem: async (orderId, data, token) => {
    const response = await axios.post(`${API_URL}/api/restaurants/orders/my/${orderId}/report-problem`,
      data,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Upload evidence photo for dispute
  uploadEvidence: async (orderId, file, token) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API_URL}/api/restaurants/orders/my/${orderId}/upload-evidence`,
      formData,
      { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  // Wallet balance
  getWalletBalance: async (token) => {
    const response = await axios.get(`${API_URL}/api/restaurants/wallet/balance`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Refund preference
  getRefundPreference: async (token) => {
    const response = await axios.get(`${API_URL}/api/restaurants/wallet/preference`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  setRefundPreference: async (method, token) => {
    const response = await axios.put(`${API_URL}/api/restaurants/wallet/preference`,
      { refundMethod: method },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Trust score
  getTrustScore: async (token) => {
    const response = await axios.get(`${API_URL}/api/restaurants/trust-score`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Contest refund (restaurant)
  contestRefund: async (orderId, reason, evidence, token) => {
    const response = await axios.post(`${API_URL}/api/restaurants/orders/${orderId}/contest-refund`,
      { reason, evidence },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Get problem categories
  getProblemCategories: async (token) => {
    const response = await axios.get(`${API_URL}/api/restaurants/orders/problem-categories`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Busy Mode / Snooze
  toggleBusyMode: async (token, snoozeMinutes = null) => {
    const body = snoozeMinutes ? { snoozeMinutes } : {};
    const response = await axios.patch(`${API_URL}/api/restaurants/busy-mode`, body, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // HMRC Tax Reporting
  getHmrcReport: async (taxYear, token) => {
    const url = taxYear
      ? `${API_URL}/api/restaurants/hmrc-report?tax_year=${taxYear}`
      : `${API_URL}/api/restaurants/hmrc-report`;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Blacklist a rider from restaurant deliveries
  blacklistRider: async (orderId, token) => {
    const response = await axios.post(`${API_URL}/api/restaurants/orders/${orderId}/blacklist-rider`, {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // No rider found — restaurant refunds guest
  noRiderRefund: async (orderId, token) => {
    const response = await axios.post(`${API_URL}/api/restaurants/orders/${orderId}/no-rider-refund`, {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // --- Delivery Schedule (Hybrid Fulfillment Toggle) ---
  getDeliverySchedule: async (token) => {
    const response = await axios.get(`${API_URL}/api/restaurants/delivery-schedule`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  updateDeliverySchedule: async (schedule, token) => {
    const response = await axios.put(`${API_URL}/api/restaurants/delivery-schedule`,
      { schedule },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // --- Restaurant Staff / Driver Management ---
  getStaff: async (token) => {
    const response = await axios.get(`${API_URL}/api/restaurants/staff`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  addStaff: async (data, token) => {
    const response = await axios.post(`${API_URL}/api/restaurants/staff`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  removeStaff: async (staffId, token) => {
    const response = await axios.delete(`${API_URL}/api/restaurants/staff/${staffId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  }
};


// Driver API
export const driverAPI = {
  // Registration
  register: async (data) => {
    const response = await axios.post(`${API_URL}/api/drivers/register`, withAcquisition(data));
    return response.data;
  },

  // Dashboard
  getDashboard: async (token) => {
    const response = await axios.get(`${API_URL}/api/drivers/dashboard`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Profile
  getProfile: async (token) => {
    const response = await axios.get(`${API_URL}/api/drivers/profile`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  updateProfile: async (data, token) => {
    const response = await axios.patch(`${API_URL}/api/drivers/profile`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  toggleAvailability: async (token) => {
    const response = await axios.patch(`${API_URL}/api/drivers/availability`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Rides - Driver
  getAvailableRides: async (token) => {
    const response = await axios.get(`${API_URL}/api/drivers/rides/available`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getMyRides: async (token, status = null) => {
    const url = status 
      ? `${API_URL}/api/drivers/rides/my?status=${status}`
      : `${API_URL}/api/drivers/rides/my`;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  acceptRide: async (rideId, token) => {
    const response = await axios.patch(`${API_URL}/api/drivers/rides/${rideId}/accept`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  updateRideStatus: async (rideId, status, token, completionPin = null, cancellationReason = null, guestLocation = null) => {
    const body = { status };
    if (completionPin) body.completionPin = completionPin;
    if (cancellationReason) body.cancellationReason = cancellationReason;
    if (guestLocation) {
      body.guestLat = guestLocation.lat;
      body.guestLng = guestLocation.lng;
    }
    const response = await axios.patch(`${API_URL}/api/drivers/rides/${rideId}/status`,
      body,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // OSRM Route
  getRoute: async (pickupLat, pickupLng, dropoffLat, dropoffLng, token) => {
    const response = await axios.post(`${API_URL}/api/drivers/route`,
      { pickupLat, pickupLng, dropoffLat, dropoffLng },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Live Share ETA
  createShareLink: async (rideId, token) => {
    const response = await axios.post(`${API_URL}/api/drivers/rides/${rideId}/share-link`, {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  getSharedTracking: async (shareToken) => {
    const response = await axios.get(`${API_URL}/api/drivers/rides/shared/${shareToken}`);
    return response.data;
  },

  // Journey Log CSV Export
  exportJourneyLog: async (token, months = 12) => {
    const response = await axios.get(`${API_URL}/api/drivers/journey-log/export?months=${months}`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob'
    });
    return response.data;
  },

  // Rides - Guest
  requestRide: async (rideData, token) => {
    const response = await axios.post(`${API_URL}/api/drivers/rides/request`, rideData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getMyRideRequests: async (token) => {
    const response = await axios.get(`${API_URL}/api/drivers/rides/my-requests`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getRideRequest: async (rideId, token) => {
    const response = await axios.get(`${API_URL}/api/drivers/rides/my-requests/${rideId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  cancelRideRequest: async (rideId, token, reason = null) => {
    const body = reason ? { reason } : {};
    const response = await axios.patch(`${API_URL}/api/drivers/rides/my-requests/${rideId}/cancel`, body, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // --- New Uber-like endpoints ---

  // Fare estimation
  getFareEstimate: async (pickupLat, pickupLng, dropoffLat, dropoffLng, vehicleType, token) => {
    const response = await axios.post(`${API_URL}/api/drivers/fare-estimate`, {
      pickupLat, pickupLng, dropoffLat, dropoffLng, vehicleType
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Geocoding
  geocode: async (address, token) => {
    const response = await axios.post(`${API_URL}/api/drivers/geocode`, { address }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  reverseGeocode: async (lat, lng, token) => {
    const response = await axios.post(`${API_URL}/api/drivers/reverse-geocode`, { lat, lng }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Real-time tracking
  trackRide: async (rideId, token) => {
    const response = await axios.get(`${API_URL}/api/drivers/rides/${rideId}/track`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Driver location update (with traffic flag)
  updateLocation: async (lat, lng, heading, speed, token, inTraffic = false) => {
    const response = await axios.post(`${API_URL}/api/drivers/location`, {
      lat, lng, heading, speed, inTraffic
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Check real traffic conditions via TomTom Traffic Flow API
  checkTraffic: async (lat, lng, token) => {
    const response = await axios.get(`${API_URL}/api/drivers/traffic-check?lat=${lat}&lng=${lng}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Nearby drivers (for showing cars on map)
  getNearbyDrivers: async (lat, lng, token) => {
    const response = await axios.get(`${API_URL}/api/drivers/nearby?lat=${lat}&lng=${lng}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Rate a completed ride
  rateRide: async (rideId, rating, comment, token) => {
    const response = await axios.post(`${API_URL}/api/drivers/rides/my-requests/${rideId}/rate`,
      { rating, comment },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Lost item report (guest side)
  reportLostItem: async (rideId, description, token) => {
    const response = await axios.post(`${API_URL}/api/drivers/rides/my-requests/${rideId}/lost-item`,
      { description },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Driver earnings
  getEarnings: async (period, token) => {
    const response = await axios.get(`${API_URL}/api/drivers/earnings?period=${period}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // UK Compliance
  getCompliance: async (token) => {
    const response = await axios.get(`${API_URL}/api/drivers/compliance`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getInsuranceStatus: async (token) => {
    const response = await axios.get(`${API_URL}/api/drivers/insurance-status`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // HMRC Tax Reporting
  getHmrcReport: async (taxYear, token) => {
    const url = taxYear
      ? `${API_URL}/api/drivers/hmrc-report?tax_year=${taxYear}`
      : `${API_URL}/api/drivers/hmrc-report`;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getTaxEstimate: async (token) => {
    const response = await axios.get(`${API_URL}/api/drivers/tax-estimate`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // --- Unified Super App Endpoints ---

  // Service preferences (taxi/delivery toggles, stacking opt-in)
  getServicePreferences: async (token) => {
    const response = await axios.get(`${API_URL}/api/drivers/service-preferences`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  updateServicePreferences: async (data, token) => {
    const response = await axios.patch(`${API_URL}/api/drivers/service-preferences`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Add a new service type (upgrade path)
  addService: async (serviceType, token) => {
    const response = await axios.post(`${API_URL}/api/drivers/add-service`, { serviceType }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Unified jobs (merged taxi + delivery)
  getUnifiedJobs: async (limit, token) => {
    const url = limit
      ? `${API_URL}/api/drivers/unified-jobs?limit=${limit}`
      : `${API_URL}/api/drivers/unified-jobs`;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Stacked job offers
  getStackedOffer: async (token) => {
    const response = await axios.get(`${API_URL}/api/drivers/stacked-offer`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  acceptStackedOffer: async (jobId, token) => {
    const response = await axios.post(`${API_URL}/api/drivers/stacked-offer/${jobId}/accept`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  declineStackedOffer: async (jobId, token) => {
    const response = await axios.post(`${API_URL}/api/drivers/stacked-offer/${jobId}/decline`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  cancelStackedOffer: async (jobId, token) => {
    const response = await axios.post(`${API_URL}/api/drivers/stacked-offer/${jobId}/cancel-driver`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // UK Journey Log (12-month retention — PHV compliance)
  getJourneyLog: async (token, months = 12, page = 1, limit = 50) => {
    const response = await axios.get(
      `${API_URL}/api/drivers/journey-log?months=${months}&page=${page}&limit=${limit}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  getJourneyLogSummary: async (token) => {
    const response = await axios.get(`${API_URL}/api/drivers/journey-log/summary`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Fatigue tracking
  getFatigueStatus: async (token) => {
    const response = await axios.get(`${API_URL}/api/drivers/fatigue-status`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Unified earnings (taxi + delivery combined)
  getUnifiedEarnings: async (period, token) => {
    const url = period
      ? `${API_URL}/api/drivers/unified-earnings?period=${period}`
      : `${API_URL}/api/drivers/unified-earnings`;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Current active job (across both services)
  getCurrentJob: async (token) => {
    const response = await axios.get(`${API_URL}/api/drivers/current-job`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  }
};


// Delivery Rider API
export const deliveryAPI = {
  // Registration
  register: async (data) => {
    const response = await axios.post(`${API_URL}/api/delivery/riders/register`, withAcquisition(data));
    return response.data;
  },

  // Dashboard & Profile
  getDashboard: async (token) => {
    const response = await axios.get(`${API_URL}/api/delivery/riders/dashboard`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getProfile: async (token) => {
    const response = await axios.get(`${API_URL}/api/delivery/riders/profile`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  updateProfile: async (data, token) => {
    const response = await axios.patch(`${API_URL}/api/delivery/riders/profile`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Availability
  toggleAvailability: async (token) => {
    const response = await axios.patch(`${API_URL}/api/delivery/riders/availability`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Location broadcast
  updateLocation: async (lat, lng, heading, speed, token) => {
    const response = await axios.post(`${API_URL}/api/delivery/riders/location`,
      { lat, lng, heading, speed },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Available deliveries to accept
  getAvailableDeliveries: async (token) => {
    const response = await axios.get(`${API_URL}/api/delivery/available`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Accept a delivery assignment
  acceptDelivery: async (assignmentId, token) => {
    const response = await axios.patch(`${API_URL}/api/delivery/accept/${assignmentId}`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Update delivery status (picked_up → en_route → delivered)
  updateDeliveryStatus: async (assignmentId, status, token, extras = {}) => {
    const response = await axios.patch(`${API_URL}/api/delivery/status/${assignmentId}`,
      { status, ...extras },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // My active and past deliveries
  getMyDeliveries: async (token) => {
    const response = await axios.get(`${API_URL}/api/delivery/my-deliveries`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Earnings
  getEarnings: async (period, token) => {
    const url = period
      ? `${API_URL}/api/delivery/riders/earnings?period=${period}`
      : `${API_URL}/api/delivery/riders/earnings`;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Track a specific delivery order (for riders to see the map)
  trackDelivery: async (orderId, token) => {
    const response = await axios.get(`${API_URL}/api/delivery/track/${orderId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Delivery fee quote (pre-order estimation)
  getDeliveryQuote: async (restaurantId, deliveryLatitude, deliveryLongitude, token) => {
    const response = await axios.post(`${API_URL}/api/delivery/quote`,
      { restaurantId, deliveryLatitude, deliveryLongitude },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Update delivery status with proof of delivery
  completeDelivery: async (assignmentId, customerPin, token) => {
    const body = { status: 'delivered', customerPin };
    const response = await axios.patch(`${API_URL}/api/delivery/status/${assignmentId}`,
      body,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Driver ledger (financial history)
  getLedger: async (weekId, token) => {
    const url = weekId
      ? `${API_URL}/api/delivery/riders/ledger?weekId=${weekId}`
      : `${API_URL}/api/delivery/riders/ledger`;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Current week earnings
  getCurrentWeekEarnings: async (token) => {
    const response = await axios.get(`${API_URL}/api/delivery/riders/earnings/current-week`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Weekly statements (payout history)
  getStatements: async (token) => {
    const response = await axios.get(`${API_URL}/api/delivery/riders/statements`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Per-delivery invoices
  getInvoices: async (weekId, token) => {
    const url = weekId
      ? `${API_URL}/api/delivery/riders/invoices?weekId=${weekId}`
      : `${API_URL}/api/delivery/riders/invoices`;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Download weekly statement as HTML
  downloadStatement: async (weekId, token) => {
    const response = await axios.get(`${API_URL}/api/delivery/riders/statements/${weekId}/download`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'text'
    });
    return response.data;
  },

  // UK Compliance
  getCompliance: async (token) => {
    const response = await axios.get(`${API_URL}/api/delivery/riders/compliance`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getInsuranceStatus: async (token) => {
    const response = await axios.get(`${API_URL}/api/delivery/riders/insurance-status`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // HMRC Tax Reporting
  getHmrcReport: async (taxYear, token) => {
    const url = taxYear
      ? `${API_URL}/api/delivery/riders/hmrc-report?tax_year=${taxYear}`
      : `${API_URL}/api/delivery/riders/hmrc-report`;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // SOS / Emergency report
  reportSOS: async (assignmentId, reason, description, lat, lng, token) => {
    const response = await axios.post(`${API_URL}/api/delivery/sos/${assignmentId}`,
      { reason, description, lat, lng },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Upload SOS evidence (photos, receipts, medical docs)
  uploadSosEvidence: async (assignmentId, files, token) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    const response = await axios.post(`${API_URL}/api/delivery/sos/${assignmentId}/evidence`,
      formData,
      { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  // Confirm stillness check (rider is OK)
  confirmStillness: async (assignmentId, token) => {
    const response = await axios.post(`${API_URL}/api/delivery/stillness-confirm/${assignmentId}`, {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Get rider notifications (for stillness checks etc.)
  getNotifications: async (token) => {
    const response = await axios.get(`${API_URL}/api/delivery/notifications`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Ensure conversation exists for rider↔guest messaging
  ensureConversation: async (assignmentId, token) => {
    const response = await axios.post(`${API_URL}/api/delivery/ensure-conversation/${assignmentId}`, {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Self-reinstate after SOS hold (for testing/demo)
  selfReinstate: async (token) => {
    const response = await axios.post(`${API_URL}/api/delivery/riders/self-reinstate`, {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

};


// Stripe Connect API
export const connectAPI = {
  // Get commission rates
  getCommissionRates: async () => {
    const response = await axios.get(`${API_URL}/api/connect/commission-rates`);
    return response.data;
  },

  // Initiate onboarding for a role
  initiateOnboarding: async (role, returnUrl, refreshUrl, token) => {
    const response = await axios.post(`${API_URL}/api/connect/onboard/${role}`, {
      returnUrl,
      refreshUrl
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Get connect account status
  getStatus: async (role, token) => {
    const response = await axios.get(`${API_URL}/api/connect/status/${role}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Get Stripe Express dashboard link
  getDashboardLink: async (role, token) => {
    const response = await axios.get(`${API_URL}/api/connect/dashboard/${role}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  }
};


// Financial Ledger API
export const ledgerAPI = {
  // Get my earnings summary
  getMyEarnings: async (token) => {
    const response = await axios.get(`${API_URL}/api/ledger/my-earnings`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Preview booking commission
  calculateBooking: async (nightsSubtotal, cleaningFee, token) => {
    const response = await axios.get(
      `${API_URL}/api/ledger/calculate/booking?nights_subtotal=${nightsSubtotal}&cleaning_fee=${cleaningFee}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Get revenue analytics from ledger
  getRevenueAnalytics: async (period, token) => {
    const response = await axios.get(
      `${API_URL}/api/ledger/analytics/revenue?period=${period}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Host Earnings Dashboard endpoints
  getHostSummary: async (token) => {
    const response = await axios.get(`${API_URL}/api/ledger/host/summary`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getHostHistory: async (token, page = 1, limit = 20, statusFilter = null) => {
    let url = `${API_URL}/api/ledger/host/history?page=${page}&limit=${limit}`;
    if (statusFilter) {
      url += `&status_filter=${statusFilter}`;
    }
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getHostPayouts: async (token, periodType = 'monthly') => {
    const response = await axios.get(
      `${API_URL}/api/ledger/host/payouts?period_type=${periodType}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  getHostMonthlyChart: async (token, months = 12) => {
    const response = await axios.get(
      `${API_URL}/api/ledger/host/monthly-chart?months=${months}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  }
};


// Subscription API
export const subscriptionAPI = {
  // Get subscription status
  getStatus: async (token) => {
    const response = await axios.get(`${API_URL}/api/subscriptions/status`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Check analytics access
  checkAnalyticsAccess: async (token) => {
    const response = await axios.get(`${API_URL}/api/subscriptions/check-analytics-access`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Create subscription checkout
  createCheckout: async (plan, originUrl, token) => {
    const response = await axios.post(`${API_URL}/api/subscriptions/checkout`, {
      plan,
      originUrl
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Check subscription checkout status
  getCheckoutStatus: async (sessionId, token) => {
    const response = await axios.get(`${API_URL}/api/subscriptions/checkout/status/${sessionId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Get subscription history
  getHistory: async (token) => {
    const response = await axios.get(`${API_URL}/api/subscriptions/history`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  }
};


// Analytics API (Subscription-gated BI)
export const analyticsAPI = {
  // Overview
  getOverview: async (token) => {
    const response = await axios.get(`${API_URL}/api/analytics/overview`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Revenue Intelligence
  getRevenue: async (token, period = '12months') => {
    const response = await axios.get(`${API_URL}/api/analytics/revenue?period=${period}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Profit Intelligence (C2)
  getProfit: async (token, period = '12months') => {
    const response = await axios.get(`${API_URL}/api/analytics/profit?period=${period}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Profit Graph APIs (C1/C2)
  rebuildProfitGraph: async (token) => {
    const response = await axios.post(`${API_URL}/api/analytics/profit-graph/rebuild`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getProfitSummary: async (token, range = '12months') => {
    const response = await axios.get(`${API_URL}/api/analytics/profit-graph/summary?range=${range}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getProfitStays: async (token, range = '12months', page = 1, limit = 50) => {
    const response = await axios.get(`${API_URL}/api/analytics/profit-graph/stays?range=${range}&page=${page}&limit=${limit}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getProfitByProperty: async (token, range = '12months', sortBy = 'netProfit') => {
    const response = await axios.get(`${API_URL}/api/analytics/profit-graph/properties?range=${range}&sort_by=${sortBy}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getProfitCostBreakdown: async (token, range = '12months') => {
    const response = await axios.get(`${API_URL}/api/analytics/profit-graph/cost-breakdown?range=${range}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getProfitMarginTrend: async (token, range = '12months') => {
    const response = await axios.get(`${API_URL}/api/analytics/profit-graph/margin-trend?range=${range}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Vendor Intelligence (C3)
  recomputeVendorScores: async (token) => {
    const response = await axios.post(`${API_URL}/api/analytics/vendors/recompute`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getVendorLeaderboard: async (token, sortBy = 'score', sortOrder = 'desc') => {
    const response = await axios.get(`${API_URL}/api/analytics/vendors/leaderboard?sort_by=${sortBy}&sort_order=${sortOrder}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getVendorDetail: async (token, vendorId) => {
    const response = await axios.get(`${API_URL}/api/analytics/vendors/${vendorId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getVendorStatsSummary: async (token) => {
    const response = await axios.get(`${API_URL}/api/analytics/vendors/stats/summary`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Vendor Intelligence (legacy)
  getVendors: async (token, period = '12months') => {
    const response = await axios.get(`${API_URL}/api/analytics/vendors?period=${period}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Upsell Intelligence (C4)
  getUpsellSummary: async (token, range = '12months') => {
    const response = await axios.get(`${API_URL}/api/analytics/upsell/summary?range=${range}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getUpsellByProperty: async (token, range = '12months') => {
    const response = await axios.get(`${API_URL}/api/analytics/upsell/by-property?range=${range}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getUpsellByBooking: async (token, range = '12months', page = 1, limit = 50) => {
    const response = await axios.get(`${API_URL}/api/analytics/upsell/by-booking?range=${range}&page=${page}&limit=${limit}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getGuestLTV: async (token, range = 'all', limit = 50) => {
    const response = await axios.get(`${API_URL}/api/analytics/upsell/guest-ltv?range=${range}&limit=${limit}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Upsell Intelligence (legacy)
  getUpsell: async (token, period = '12months') => {
    const response = await axios.get(`${API_URL}/api/analytics/upsell?period=${period}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Deal ROI Intelligence (C5)
  getDealsSummary: async (token) => {
    const response = await axios.get(`${API_URL}/api/analytics/deals/summary`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getDealsComparison: async (token, sortBy = 'roiPct', sortOrder = 'desc') => {
    const response = await axios.get(`${API_URL}/api/analytics/deals/comparison?sort_by=${sortBy}&sort_order=${sortOrder}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getDealDetail: async (token, dealId) => {
    const response = await axios.get(`${API_URL}/api/analytics/deals/${dealId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Deal ROI Intelligence (legacy)
  getDeals: async (token) => {
    const response = await axios.get(`${API_URL}/api/analytics/deals`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  }
};


// Admin API (Platform monitoring - read-only)
export const adminAPI = {
  // Admin alert centre. Backed by the admin_alerts MongoDB collection
  // that the referral milestones (250 / 500) and the Stripe webhook
  // stubs (payout.failed, dispute, account.updated) write into.
  getAlerts: async (token, includeResolved = false) => {
    const response = await axios.get(
      `${API_URL}/api/admin/alerts?include_resolved=${includeResolved ? 'true' : 'false'}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  resolveAlert: async (alertId, token) => {
    const response = await axios.patch(
      `${API_URL}/api/admin/alerts/${alertId}/resolve`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Financial Overview
  getFinancialOverview: async (token, period = '30days') => {
    const response = await axios.get(`${API_URL}/api/admin/financial/overview?period=${period}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getMonthlyTrend: async (token, months = 12) => {
    const response = await axios.get(`${API_URL}/api/admin/financial/monthly-trend?months=${months}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Commission Audit
  getCommissionAudit: async (token, period = '30days', groupBy = 'beneficiaryRole') => {
    const response = await axios.get(`${API_URL}/api/admin/financial/commission-audit?period=${period}&group_by=${groupBy}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getTopEarners: async (token, period = '30days', role = 'host', limit = 20) => {
    const response = await axios.get(`${API_URL}/api/admin/financial/top-earners?period=${period}&role=${role}&limit=${limit}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Risk Monitoring
  getRiskMonitoring: async (token, period = '30days') => {
    const response = await axios.get(`${API_URL}/api/admin/risk/monitoring?period=${period}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Subscription Tracking
  getSubscriptionsOverview: async (token) => {
    const response = await axios.get(`${API_URL}/api/admin/subscriptions/overview`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getSubscriptionsList: async (token, status = 'active', page = 1, limit = 50) => {
    const response = await axios.get(`${API_URL}/api/admin/subscriptions/list?status=${status}&page=${page}&limit=${limit}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Stripe Connect Status
  getConnectStatus: async (token) => {
    const response = await axios.get(`${API_URL}/api/admin/connect/status`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  getPendingOnboarding: async (token, role = 'host', limit = 50) => {
    const response = await axios.get(`${API_URL}/api/admin/connect/pending-onboarding?role=${role}&limit=${limit}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Platform Stats
  getPlatformStats: async (token) => {
    const response = await axios.get(`${API_URL}/api/admin/stats/overview`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Restaurant Verification Pipeline
  getVerificationPipeline: async (token) => {
    const response = await axios.get(`${API_URL}/api/restaurants/admin/verification-pipeline`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  verifyFSA: async (restaurantId, token) => {
    const response = await axios.post(`${API_URL}/api/restaurants/admin/verify-fsa/${restaurantId}`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  verifyVAT: async (restaurantId, vatNumber, token) => {
    const response = await axios.post(`${API_URL}/api/restaurants/admin/verify-vat/${restaurantId}`, { vatNumber }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  pipelineApprove: async (restaurantId, notes, token) => {
    const response = await axios.post(`${API_URL}/api/restaurants/admin/pipeline-approve/${restaurantId}`, { notes }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Weekly Payouts
  getWeeklyPayoutRuns: async (token) => {
    const response = await axios.get(`${API_URL}/api/admin/payouts/weekly-runs`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  triggerWeeklyPayout: async (token) => {
    const response = await axios.post(`${API_URL}/api/admin/payouts/trigger-weekly`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Disputes & Refunds
  getDisputes: async (status, token) => {
    const url = status ? `${API_URL}/api/restaurants/admin/disputes?status=${status}` : `${API_URL}/api/restaurants/admin/disputes`;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  resolveDispute: async (orderId, decision, notes, token) => {
    const response = await axios.post(`${API_URL}/api/restaurants/admin/resolve-dispute/${orderId}`,
      { decision, notes },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // --- Risk & Fraud ---
  getFlaggedRiders: async (token) => {
    const response = await axios.get(`${API_URL}/api/admin/risk/flagged-riders`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  getDeliveryRiders: async (token) => {
    const response = await axios.get(`${API_URL}/api/admin/delivery/riders`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  getDriversList: async (token) => {
    const response = await axios.get(`${API_URL}/api/admin/drivers/list`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  getRestaurantsList: async (token) => {
    const response = await axios.get(`${API_URL}/api/admin/restaurants/list`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  setRestaurantPlatformDeliveryReady: async (restaurantId, ready, token) => {
    const response = await axios.patch(
      `${API_URL}/api/admin/restaurants/${restaurantId}/platform-delivery-ready`,
      { ready },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },
  getProvidersList: async (token) => {
    const response = await axios.get(`${API_URL}/api/admin/providers/list`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  getGpsDeviations: async (token) => {
    const response = await axios.get(`${API_URL}/api/admin/risk/gps-deviations`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  getIncidents: async (type, status, token) => {
    const params = new URLSearchParams();
    if (type) params.append('incident_type', type);
    if (status) params.append('status', status);
    const response = await axios.get(`${API_URL}/api/admin/risk/incidents?${params}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  reviewClaim: async (incidentId, action, notes, token) => {
    const response = await axios.post(`${API_URL}/api/admin/risk/review-claim/${incidentId}`,
      { action, notes },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },
  flagRiderIncident: async (riderId, data, token) => {
    const response = await axios.post(`${API_URL}/api/delivery/riders/${riderId}/flag-incident`,
      data,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // ── Superuser endpoints ──
  getUsers: async (token, { role, search, page = 1, limit = 50 } = {}) => {
    const params = new URLSearchParams({ page, limit });
    if (role) params.set('role', role);
    if (search) params.set('search', search);
    const response = await axios.get(`${API_URL}/api/admin/users?${params}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  updateUserRoles: async (userId, action, role, token) => {
    const response = await axios.patch(`${API_URL}/api/admin/users/${userId}/roles`,
      { action, role },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },
  suspendUser: async (userId, suspended, token) => {
    const response = await axios.patch(`${API_URL}/api/admin/users/${userId}/suspend`,
      { suspended },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },
  deleteUser: async (userId, token) => {
    const response = await axios.delete(`${API_URL}/api/admin/users/${userId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },
  getAdminProperties: async (token, { search, hostId, page = 1, limit = 50 } = {}) => {
    const params = new URLSearchParams({ page, limit });
    if (search) params.set('search', search);
    if (hostId) params.set('host_id', hostId);
    const response = await axios.get(`${API_URL}/api/admin/properties?${params}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  getAdminBookings: async (token, { status, search, page = 1, limit = 50 } = {}) => {
    const params = new URLSearchParams({ page, limit });
    if (status) params.set('status', status);
    if (search) params.set('search', search);
    const response = await axios.get(`${API_URL}/api/admin/bookings?${params}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  getAdminMarketplaceListings: async (token, { search, status, page = 1, limit = 50 } = {}) => {
    const params = new URLSearchParams({ page, limit });
    if (status) params.set('status', status);
    if (search) params.set('search', search);
    const response = await axios.get(`${API_URL}/api/admin/marketplace/listings?${params}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  getAdminMarketplaceOffers: async (token, { status, page = 1, limit = 50 } = {}) => {
    const params = new URLSearchParams({ page, limit });
    if (status) params.set('status', status);
    const response = await axios.get(`${API_URL}/api/admin/marketplace/offers?${params}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  getRecentActivity: async (token, limit = 50) => {
    const response = await axios.get(`${API_URL}/api/admin/activity/recent?limit=${limit}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Ledger — platform-wide transaction analytics
  getLedgerPlatformAnalytics: async (token) => {
    const response = await axios.get(`${API_URL}/api/ledger/analytics/platform`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Ledger — recent entries (all)
  getLedgerRecentEntries: async (token, limit = 100) => {
    const response = await axios.get(`${API_URL}/api/ledger/my-earnings?limit=${limit}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // User Timeline — unified history for a single user across all verticals
  getUserTimeline: async (userId, token) => {
    const response = await axios.get(`${API_URL}/api/admin/users/${userId}/timeline`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // --- Financial Balance Check ---
  getBalanceCheck: async (token, period = 'all') => {
    const response = await axios.get(`${API_URL}/api/admin/financial/balance-check?period=${period}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // --- Payout Pre-Flight ---
  getPayoutPreflight: async (token, riskThreshold = 50) => {
    const response = await axios.get(
      `${API_URL}/api/admin/payouts/preflight?risk_threshold=${riskThreshold}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // --- VAT / HMRC Export (triggers browser download) ---
  exportVatCsv: (token, period = '30days') => {
    const url = `${API_URL}/api/admin/ledger/vat-export?period=${period}`;
    const link = document.createElement('a');
    link.href = url;
    // Pass token via query string for file downloads (Authorization header not supported in anchor tags)
    link.href = `${url}&token=${token}`;
    link.download = '';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },

  // --- Platform-Wide Disputes ---
  getAllDisputes: async (token, status = '') => {
    const url = status
      ? `${API_URL}/api/admin/disputes?status=${status}`
      : `${API_URL}/api/admin/disputes`;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  initiateDisputeRefund: async (disputeId, notes, token) => {
    const response = await axios.post(
      `${API_URL}/api/admin/disputes/${disputeId}/initiate-refund`,
      { notes },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  markDisputeLegal: async (disputeId, notes, token) => {
    const response = await axios.post(
      `${API_URL}/api/admin/disputes/${disputeId}/mark-legal`,
      { notes },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  closeDispute: async (disputeId, notes, token) => {
    const response = await axios.post(
      `${API_URL}/api/admin/disputes/${disputeId}/close`,
      { notes },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // --- Internal Notes (Admin-Private) ---
  getInternalNotes: async (targetId, targetType, token) => {
    const response = await axios.get(
      `${API_URL}/api/admin/internal-notes?target_id=${targetId}&target_type=${targetType}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  addInternalNote: async (targetId, targetType, content, token) => {
    const response = await axios.post(
      `${API_URL}/api/admin/internal-notes`,
      { targetId, targetType, content },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  getUploadPresignedUrl: async (targetId, targetType, filename, token) => {
    const response = await axios.post(
      `${API_URL}/api/admin/internal-notes/presign-upload`,
      { targetId, targetType, filename },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // --- Audit Log ---
  getAuditLog: async (token, { action, targetType, adminId, search, sinceDays, page = 1, limit = 50 } = {}) => {
    const params = new URLSearchParams();
    if (action)     params.append('action',      action);
    if (targetType) params.append('target_type', targetType);
    if (adminId)    params.append('admin_id',    adminId);
    if (search)     params.append('search',      search);
    if (sinceDays)  params.append('since_days',  String(sinceDays));
    params.append('page',  String(page));
    params.append('limit', String(limit));
    const response = await axios.get(
      `${API_URL}/api/admin/audit-log?${params}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // --- Soft Launch ---
  getBetaUsers: async (token) => {
    const response = await axios.get(
      `${API_URL}/api/admin/soft-launch/beta-users`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  setBetaFlag: async (userId, flag, notes, token) => {
    const params = new URLSearchParams({ flag, ...(notes ? { notes } : {}) });
    const response = await axios.post(
      `${API_URL}/api/admin/soft-launch/beta-users/${userId}?${params}`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  getFrictionFeed: async (token, hours = 24) => {
    const response = await axios.get(
      `${API_URL}/api/admin/soft-launch/friction-feed?hours=${hours}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // --- Growth Metrics ---
  getConversionFunnel: async (token, period = '30days') => {
    const response = await axios.get(
      `${API_URL}/api/admin/growth/funnel?period=${period}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  getVerticalPerformance: async (token, period = '30days') => {
    const response = await axios.get(
      `${API_URL}/api/admin/growth/vertical-performance?period=${period}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // --- Fraud Signals ---
  getFraudSignals: async (token) => {
    const response = await axios.get(
      `${API_URL}/api/admin/fraud/signals`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // --- Retention ---
  getLowRatings: async (token, threshold = 3) => {
    const response = await axios.get(
      `${API_URL}/api/admin/retention/low-ratings?threshold=${threshold}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  getStalledUsers: async (token) => {
    const response = await axios.get(
      `${API_URL}/api/admin/retention/stalled-users`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // --- Incentives ---
  getIncentives: async (token, { userId, active } = {}) => {
    const params = new URLSearchParams();
    if (userId !== undefined) params.append('user_id', userId);
    if (active !== undefined) params.append('active',  active);
    const response = await axios.get(
      `${API_URL}/api/admin/incentives?${params}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  createIncentive: async (body, token) => {
    const response = await axios.post(
      `${API_URL}/api/admin/incentives`,
      body,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  updateIncentive: async (incentiveId, updates, token) => {
    const params = new URLSearchParams();
    if (updates.active !== undefined) params.append('active', updates.active);
    if (updates.notes  !== undefined) params.append('notes',  updates.notes);
    const response = await axios.patch(
      `${API_URL}/api/admin/incentives/${incentiveId}?${params}`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  revokeIncentive: async (incentiveId, token) => {
    const response = await axios.delete(
      `${API_URL}/api/admin/incentives/${incentiveId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // --- Referral Codes ---
  getReferralCodes: async (token) => {
    const response = await axios.get(
      `${API_URL}/api/admin/referrals`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  mintReferralCode: async (body, token) => {
    const response = await axios.post(
      `${API_URL}/api/admin/referrals/mint`,
      body,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  toggleReferralCode: async (code, token) => {
    const response = await axios.patch(
      `${API_URL}/api/admin/referrals/${code}/toggle`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // --- Welcome Emails ---
  // Send a one-off SAMPLE welcome to any address for proofreading (no user touched).
  sendWelcomePreview: async (body, token) => {
    const response = await axios.post(
      `${API_URL}/api/admin/welcome-emails/preview`,
      body,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // One-time welcome to existing users. dryRun=true returns the recipient list
  // without sending anything.
  backfillWelcome: async (dryRun, token) => {
    const response = await axios.post(
      `${API_URL}/api/admin/welcome-emails/backfill`,
      { dry_run: dryRun },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // --- Ambassador ---
  setAmbassador: async (userId, flag, token) => {
    const response = await axios.post(
      `${API_URL}/api/admin/users/${userId}/set-ambassador?flag=${flag}`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Drivers list for Suspension Command Centre
  getDrivers: async (token, page = 1, limit = 200) => {
    const response = await axios.get(
      `${API_URL}/api/admin/users?role=driver&page=${page}&limit=${limit}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const data = response.data;
    // Normalise: backend may return { users } or { drivers }
    return { drivers: data?.drivers || data?.users || [] };
  },

  // Restaurants list for Suspension Command Centre
  getRestaurants: async (token, page = 1, limit = 200, search = '') => {
    const params = `role=restaurant_owner&page=${page}&limit=${limit}${search ? `&search=${encodeURIComponent(search)}` : ''}`;
    const response = await axios.get(
      `${API_URL}/api/admin/users?${params}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const data = response.data;
    // Normalise: backend may return { users } or { restaurants }
    return { restaurants: data?.restaurants || data?.users || [] };
  },

  // Look up a user by their human-readable BC-XXXXX reference
  lookupUserByRef: async (userRef, token) => {
    const response = await axios.get(
      `${API_URL}/api/admin/users/lookup-by-ref/${encodeURIComponent(userRef.trim().toUpperCase())}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;  // { user: { _id, name, email, userRef, ... } }
  },

  // ── Platform Settings (kill-switches + signup gates) ──────────────────
  getSettings: async (token) => {
    const response = await axios.get(`${API_URL}/api/admin/settings`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  updateSettings: async (patch, token) => {
    const response = await axios.patch(`${API_URL}/api/admin/settings`, patch, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
};


// Inventory Management API
export const inventoryAPI = {
  // Dashboard summary
  getDashboard: async (token) => {
    const response = await axios.get(`${API_URL}/api/inventory/dashboard`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Templates
  getTemplates: async (token) => {
    const response = await axios.get(`${API_URL}/api/inventory/templates`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  applyTemplate: async (propertyId, templateKey, token) => {
    const response = await axios.post(`${API_URL}/api/inventory/properties/${propertyId}/apply-template`,
      { templateKey },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Property items
  getPropertyItems: async (propertyId, token, category = null) => {
    const url = category
      ? `${API_URL}/api/inventory/properties/${propertyId}/items?category=${category}`
      : `${API_URL}/api/inventory/properties/${propertyId}/items`;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // CRUD
  createItem: async (data, token) => {
    const response = await axios.post(`${API_URL}/api/inventory/items`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  updateItem: async (itemId, data, token) => {
    const response = await axios.patch(`${API_URL}/api/inventory/items/${itemId}`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  deleteItem: async (itemId, token) => {
    const response = await axios.delete(`${API_URL}/api/inventory/items/${itemId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Stock operations
  restockItem: async (itemId, quantity, notes, token) => {
    const response = await axios.post(`${API_URL}/api/inventory/items/${itemId}/restock`,
      { quantity, notes },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  adjustStock: async (itemId, quantity, reason, token) => {
    const response = await axios.post(`${API_URL}/api/inventory/items/${itemId}/adjust`,
      { quantity, reason },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Host dismisses a cleaner's low-stock flag on an item (without restocking).
  clearReport: async (itemId, token) => {
    const response = await axios.post(`${API_URL}/api/inventory/items/${itemId}/clear-report`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Auto-deduct for booking
  autoDeduct: async (bookingId, token) => {
    const response = await axios.post(`${API_URL}/api/inventory/auto-deduct/${bookingId}`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Task-based inventory deduction (cleaning/linen tasks)
  taskDeduct: async (taskId, token) => {
    const response = await axios.post(`${API_URL}/api/inventory/task-deduct/${taskId}`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Alerts
  getAlerts: async (token) => {
    const response = await axios.get(`${API_URL}/api/inventory/alerts`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Forecast
  getForecast: async (token, days = 30) => {
    const response = await axios.get(`${API_URL}/api/inventory/forecast?days=${days}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Logs
  getLogs: async (token, propertyId = null, itemId = null, logType = null, page = 1) => {
    const params = new URLSearchParams({ page });
    if (propertyId) params.append('property_id', propertyId);
    if (itemId) params.append('item_id', itemId);
    if (logType) params.append('log_type', logType);
    const response = await axios.get(`${API_URL}/api/inventory/logs?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Cost analytics
  getCostAnalytics: async (token, months = 6) => {
    const response = await axios.get(`${API_URL}/api/inventory/cost-analytics?months=${months}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  }
};


// Financial Insights API
export const insightsAPI = {
  // Revenue forecast
  getRevenueForecast: async (token, monthsAhead = 3) => {
    const response = await axios.get(`${API_URL}/api/insights/revenue-forecast?months_ahead=${monthsAhead}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Cash flow projection
  getCashflow: async (token, { granularity = 'weekly', periods = 8, startDate = null } = {}) => {
    const params = new URLSearchParams({ granularity, periods: String(periods) });
    if (startDate) params.set('start_date', startDate);
    const response = await axios.get(`${API_URL}/api/insights/cashflow?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Per-property P&L
  getPropertyPnl: async (token, period = '12months') => {
    const response = await axios.get(`${API_URL}/api/insights/property-pnl?period=${period}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Expense summary
  getExpenseSummary: async (token, period = '12months') => {
    const response = await axios.get(`${API_URL}/api/insights/expense-summary?period=${period}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Smart alerts
  getSmartAlerts: async (token) => {
    const response = await axios.get(`${API_URL}/api/insights/smart-alerts`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Benchmarks
  getBenchmarks: async (token) => {
    const response = await axios.get(`${API_URL}/api/insights/benchmarks`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Set the host's own monthly earnings target for benchmarking.
  setEarningsTarget: async (token, monthlyTarget) => {
    const response = await axios.post(`${API_URL}/api/insights/earnings-target`,
      { monthlyTarget },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // AI-Driven P&L Insights
  getAiPnlInsights: async (token, period = '12months') => {
    const response = await axios.get(`${API_URL}/api/insights/ai-pnl?period=${period}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
};

// =============================================================================
// REFERRAL API — user-facing earnings & code management
// =============================================================================
export const referralAPI = {
  // Get (or generate) user's personal referral code + share link
  getMyCode: async (token) => {
    const response = await axios.get(`${API_URL}/api/referrals/my-code`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Earnings stats: paid, pending_verification, upcoming
  getStats: async (token) => {
    const response = await axios.get(`${API_URL}/api/referrals/stats`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Full referral history
  getHistory: async (token, limit = 50) => {
    const response = await axios.get(`${API_URL}/api/referrals/history?limit=${limit}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Reward History Ledger — EARNING + SAVING rows + aggregate totals
  getRewardLedger: async (token, limit = 100) => {
    const response = await axios.get(`${API_URL}/api/referrals/reward-ledger?limit=${limit}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
};

// =============================================================================
// ADMIN REFERRAL API — Command Centre, Fraud Radar, Payout management
// =============================================================================
export const adminReferralAPI = {
  // Command Centre dashboard (burn vs earn, leaderboard, live counter)
  getCommandCentre: async (token) => {
    const response = await axios.get(`${API_URL}/api/admin/referrals/command-centre`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Update campaign settings (pause switch, cap, Fraud Radar minimum)
  updateCampaign: async (settings, token) => {
    const params = new URLSearchParams();
    if (settings.newReferralsActive !== undefined) params.append('new_referrals_active', settings.newReferralsActive);
    if (settings.globalCap         !== undefined) params.append('global_cap',           settings.globalCap);
    if (settings.minGrossGbp       !== undefined) params.append('min_gross_gbp',         settings.minGrossGbp);
    const response = await axios.patch(`${API_URL}/api/admin/referrals/campaign?${params}`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Fraud Radar
  getFraudRadar: async (token) => {
    const response = await axios.get(`${API_URL}/api/admin/referrals/fraud-radar`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // All payouts (filterable by status)
  getAllPayouts: async (token, status = '') => {
    const params = status ? `?status=${status}` : '';
    const response = await axios.get(`${API_URL}/api/admin/referrals/all-payouts${params}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Void a payout
  voidPayout: async (payoutId, reason, notes, token) => {
    const response = await axios.post(
      `${API_URL}/api/admin/referrals/payouts/${payoutId}/void`,
      { reason, notes },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Approve a payout
  approvePayout: async (payoutId, token) => {
    const response = await axios.post(
      `${API_URL}/api/admin/referrals/payouts/${payoutId}/approve`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // (processCompletions and autoApproveExpired were removed 2026-10-05 with
  // the one-time welcome bonus they served.)
};

// =============================================================================
// COMPLIANCE API — User-facing appeal flow
// =============================================================================
export const complianceAPI = {
  // Get own suspension/compliance status
  getMyStatus: async (token) => {
    const response = await axios.get(`${API_URL}/api/compliance/my-status`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Submit appeal (moves SUSPENDED → UNDER_REVIEW)
  submitAppeal: async (message, documentType, token) => {
    const response = await axios.post(
      `${API_URL}/api/compliance/submit-appeal`,
      { message, documentType },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Upload evidence file to an appeal
  uploadEvidence: async (appealId, file, documentLabel, token) => {
    const form = new FormData();
    form.append('file', file);
    form.append('documentLabel', documentLabel || 'Supporting Document');
    const response = await axios.post(
      `${API_URL}/api/compliance/upload-evidence/${appealId}`,
      form,
      { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },
};

// =============================================================================
// ADMIN SUSPENSION + COMPLIANCE API
// =============================================================================
export const adminComplianceAPI = {
  // Entity kill-switch (restaurant / driver / user)
  suspendEntity: async (entityId, entityType, reason, reasonNote, suspend, token) => {
    const response = await axios.post(
      `${API_URL}/api/admin/suspend-entity`,
      { entityId, entityType, reason, reasonNote, suspend },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Health-pill summary (suspended counts)
  getSuspendedSummary: async (token) => {
    const response = await axios.get(`${API_URL}/api/admin/suspend-entity/summary`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Appeals verification pipeline
  getAppeals: async (token, status = '', page = 1) => {
    const response = await axios.get(
      `${API_URL}/api/admin/compliance/appeals?page=${page}${status ? `&status=${status}` : ''}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Decide an appeal (approve / request_info / permanent_ban)
  decideAppeal: async (appealId, decision, extra, token) => {
    const response = await axios.post(
      `${API_URL}/api/admin/compliance/appeals/${appealId}/decision`,
      { decision, ...extra },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Expiring documents list
  getExpiring: async (token, days = 7) => {
    const response = await axios.get(
      `${API_URL}/api/admin/compliance/expiring?days=${days}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Nudge all expiring entities
  nudgeAll: async (token) => {
    const response = await axios.post(
      `${API_URL}/api/admin/compliance/nudge-all`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Download CSV compliance report
  downloadReport: (token) => {
    window.open(`${API_URL}/api/admin/compliance/report?token=${token}`, '_blank');
  },

  // ── AI OCR Verification Queue ────────────────────────────────────────────

  // Get documents pending admin review (post-OCR)
  getVerificationQueue: async (token, { route, preScore } = {}) => {
    const params = new URLSearchParams();
    if (route)     params.append('route',     route);
    if (preScore)  params.append('pre_score', preScore);
    const response = await axios.get(
      `${API_URL}/api/compliance/admin/verification-queue?${params}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Single-click "Verify & Set Expiry"
  verifyDocument: async (logId, expiryDate, certNumber, notes, token) => {
    const response = await axios.post(
      `${API_URL}/api/compliance/admin/verify-document`,
      { log_id: logId, expiry_date: expiryDate, cert_number: certNumber, notes },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  // Reject document with reason
  rejectDocument: async (logId, rejectionReason, token) => {
    const response = await axios.post(
      `${API_URL}/api/compliance/admin/reject-document`,
      { log_id: logId, rejection_reason: rejectionReason },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },
};

// ── Partner Compliance API ───────────────────────────────────────────────────
export const partnerComplianceAPI = {
  // Upload a compliance document (triggers pre-flight + OCR pipeline)
  uploadDocument: async (file, documentType, token) => {
    const form = new FormData();
    form.append('file', file);
    form.append('documentType', documentType);
    const response = await axios.post(
      `${API_URL}/api/compliance/upload-document`,
      form,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },
};


// ── Guest Waitlist API (Pre-Launch Growth Engine) ────────────────────────────
export const waitlistAPI = {
  /**
   * Join the waitlist. Returns referral code + share URL immediately.
   * Idempotent — re-joining returns the existing record.
   */
  join: async ({ email, phone = null, referredBy = null, role = null }) => {
    // withAcquisition carries the src and utm_ labels through, exactly as it
    // does on a sign-up, so somebody who joins the list through a partner's
    // tracked link is still credited to them.
    const response = await axios.post(
      `${API_URL}/api/waitlist/join`,
      withAcquisition({ email, phone, referredBy, role })
    );
    return response.data;
  },

  /** Check if an email is already on the waitlist. */
  check: async (email) => {
    const response = await axios.get(
      `${API_URL}/api/waitlist/check?email=${encodeURIComponent(email.trim().toLowerCase())}`
    );
    return response.data;
  },

  // ── Admin ──────────────────────────────────────────────────────────────

  /** Waitlist stats for the Admin Liquidity panel. */
  getStats: async (token) => {
    const response = await axios.get(`${API_URL}/api/admin/waitlist/stats`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  /** Paginated waitlist list. */
  getList: async (token, { page = 1, limit = 50, search = '', referrers_only = false } = {}) => {
    const params = new URLSearchParams({
      page, limit, search,
      referrers_only: referrers_only ? 'true' : 'false',
    });
    const response = await axios.get(`${API_URL}/api/admin/waitlist/list?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  /** Send Launch Blast to all un-notified waitlist entries. */
  sendBlast: async (token) => {
    const response = await axios.post(`${API_URL}/api/admin/waitlist/blast`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
};

// FAQ Feedback API
export const faqFeedbackAPI = {
  submit: async (token, data) => {
    const response = await axios.post(`${API_URL}/api/faq-feedback`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  getAll: async (token) => {
    const response = await axios.get(`${API_URL}/api/admin/faq-feedback`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  markRead: async (token, id) => {
    const response = await axios.patch(`${API_URL}/api/admin/faq-feedback/${id}/read`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
};

// Contact form API (submit is public; getAll/markRead are admin-only)
export const contactAPI = {
  submit: async (data) => {
    const response = await axios.post(`${API_URL}/api/contact`, data);
    return response.data;
  },

  getAll: async (token) => {
    const response = await axios.get(`${API_URL}/api/admin/contact`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  markRead: async (token, id) => {
    const response = await axios.patch(`${API_URL}/api/admin/contact/${id}/read`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
};

// Cleaning turnover dispatch.
export const cleaningAPI = {
  // Public magic-link endpoints (no auth) — used by the cleaner's Accept/Decline page.
  viewOffer: async (token) => {
    const response = await axios.get(`${API_URL}/api/cleaning/offer/${token}`);
    return response.data;
  },

  acceptOffer: async (token) => {
    const response = await axios.post(`${API_URL}/api/cleaning/offer/${token}/accept`);
    return response.data;
  },

  declineOffer: async (token, reason = '') => {
    const response = await axios.post(`${API_URL}/api/cleaning/offer/${token}/decline`, { reason });
    return response.data;
  },

  // Minimal inventory list (id/name/category) for the cleaner's "running low?" checklist.
  getJobInventory: async (token) => {
    const response = await axios.get(`${API_URL}/api/cleaning/offer/${token}/inventory`);
    return response.data;
  },

  // Cleaner marks the job done, optional notes + photos + inventory report
  // (FormData: notes, files[], inventoryReport JSON string of item ids).
  completeJob: async (token, formData) => {
    const response = await axios.post(`${API_URL}/api/cleaning/offer/${token}/complete`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Host endpoints (authenticated).
  createJob: async (payload, token) => {
    const response = await axios.post(`${API_URL}/api/cleaning/jobs`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  listJobs: async (token, status) => {
    const params = status ? `?status=${encodeURIComponent(status)}` : '';
    const response = await axios.get(`${API_URL}/api/cleaning/jobs${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
};

// =============================================================================
// ADMIN ACQUISITION API — sign-ups per partner tracking link (utm_source)
// =============================================================================
export const adminAcquisitionAPI = {
  // Monthly report; month is "YYYY-MM" (defaults to the current month)
  getSummary: async (month, token) => {
    const query = month ? `?month=${encodeURIComponent(month)}` : '';
    const response = await axios.get(`${API_URL}/api/admin/acquisition/summary${query}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
};
